export type SetupSummarySnapshot = {
  tenant: { name: string; slug: string; default_timezone: string; currency: string };
  companies: Array<{ name?: string; legal_name?: string }>;
  branches: Array<{ name?: string; timezone?: string }>;
  users: Array<{ name?: string; email?: string; role?: string }>;
  workflow: { template_key?: string; template_version?: number; terminology?: unknown; mileage_rate?: unknown; expense_categories?: unknown; invoice_settings?: unknown };
  readiness: { overall_status: string; completion_pct: number; incomplete_keys: string[]; completed_at?: string | null };
};
export type SetupSummaryDocument = {
  id: string; version_number: number; generated_at: string; generated_by_name?: string | null;
  download_count: number; first_downloaded_at?: string | null; last_downloaded_at?: string | null;
  snapshot: SetupSummarySnapshot;
};

const escapePdf = (value: unknown) => String(value ?? "—")
  .replace(/\\/g, "\\\\").replace(/\(/g, "\\(").replace(/\)/g, "\\)")
  .replace(/[^\x20-\x7E]/g, "?");

export function buildTenantSetupSummaryPdf(document: SetupSummaryDocument): Blob {
  const s = document.snapshot;
  const lines = [
    "REMEDIKEEP TENANT SETUP SUMMARY",
    `Document version: ${document.version_number}`,
    `Generated at: ${document.generated_at}`,
    `Generated by: ${document.generated_by_name || "—"}`,
    "",
    `Company: ${s.tenant.name}`,
    `Workspace slug: ${s.tenant.slug}`,
    `Timezone: ${s.tenant.default_timezone}`,
    `Currency: ${s.tenant.currency}`,
    "",
    `Companies: ${s.companies.map((x) => x.name || x.legal_name).filter(Boolean).join(", ") || "None"}`,
    `Branches: ${s.branches.map((x) => x.name).filter(Boolean).join(", ") || "None"}`,
    `Active users: ${s.users.length}`,
    ...s.users.slice(0, 15).map((x) => `  - ${x.name || x.email || "Unnamed"} (${(x.role || "unassigned").replace(/_/g, " ")})`),
    "",
    `Workflow template: ${s.workflow.template_key || "Not selected"}${s.workflow.template_version ? ` v${s.workflow.template_version}` : ""}`,
    `Readiness: ${s.readiness.completion_pct}% (${s.readiness.overall_status.replace(/_/g, " ")})`,
    `Outstanding items: ${s.readiness.incomplete_keys.length ? s.readiness.incomplete_keys.join(", ") : "None"}`,
    "",
    "This document contains no passwords or temporary login credentials.",
  ];
  const stream = ["BT", "/F1 17 Tf", "48 752 Td", `(${escapePdf(lines[0])}) Tj`, "/F1 9 Tf"];
  lines.slice(1).forEach((line, index) => stream.push(`0 ${index === 0 ? -26 : -18} Td (${escapePdf(line)}) Tj`));
  stream.push("ET");
  const body = stream.join("\n");
  const objects = [
    "<< /Type /Catalog /Pages 2 0 R >>",
    "<< /Type /Pages /Kids [3 0 R] /Count 1 >>",
    "<< /Type /Page /Parent 2 0 R /MediaBox [0 0 612 792] /Resources << /Font << /F1 5 0 R >> >> /Contents 4 0 R >>",
    `<< /Length ${body.length} >>\nstream\n${body}\nendstream`,
    "<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>",
  ];
  let out = "%PDF-1.4\n"; const offsets = [0];
  objects.forEach((object, index) => { offsets.push(out.length); out += `${index + 1} 0 obj\n${object}\nendobj\n`; });
  const xref = out.length; out += `xref\n0 ${objects.length + 1}\n0000000000 65535 f \n`;
  offsets.slice(1).forEach((offset) => { out += `${String(offset).padStart(10, "0")} 00000 n \n`; });
  out += `trailer\n<< /Size ${objects.length + 1} /Root 1 0 R >>\nstartxref\n${xref}\n%%EOF`;
  return new Blob([out], { type: "application/pdf" });
}
